import React, {useContext, useMemo, useState} from 'react'
import {makeStyles, Table, TableBody, TableCell, TableHead, TableRow} from '@material-ui/core';
import Tr from 'translations/Translations';
import WorkableIconDeleteButton from 'components/button/WorkableIconDeleteButton';
import DownloadHistoryPopup, {createDownloadHistoryFile} from "components/popup/DownloadHistoryPopup";
import Confirm from "components/popup/confirm/Confirm";
import EditDescriptionPopup, {createEditDescriptionFile} from "components/popup/EditDescriptionPopup";
import SiwzService from "services/SiwzService";
import SiwzFileTableRow from "./SiwzFileTableRow";
import DemandValidationContext from "context/DemandValidationContext";
import BoldTypography from "components/BoldTypography";
import PropTypes from 'prop-types';
import PrivilegeService from "services/PrivilegeService";
import {shareSystemsActive} from "libs/shareSystems";
import {connect} from "react-redux";
import AddNoticeReferenceNumberPopup, {createEditNoticeReferenceNumberFile} from "./AddNoticeReferenceNumberPopup";

const useStyle = makeStyles(theme => ({
    lpColumn: theme.components.table.lpColumn,
    buttonColumn: theme.components.table.buttonColumn,
    dateColumn: theme.components.table.dateColumn,
    typeColumn: {
        width: 180
    }
}));

function SiwzFileTable(
    {
        demandId,
        isPublished,
        disabled,
        siwzList,
        demandPrivileges = {},
        demandHeader
    }) {

    const classes = useStyle();
    const [downloadHistory, setDownloadHistory] = useState(null);
    const [openConfirmDeleteAll, setOpenConfirmDeleteAll] = useState(false);
    const [editDescriptionFile, setEditDescriptionFile] = useState(null);
    const [editNoticeReferenceNumberFile, setEditNoticeReferenceNumberFile] = useState(null);
    const currentDemandValidation = useContext(DemandValidationContext);

    const showShare = useMemo(() => demandPrivileges.INTEGRATIONS_UPLOAD && shareSystemsActive(demandHeader).length > 0, [demandHeader, demandPrivileges])

    const onDeleteAll = () => {
        setOpenConfirmDeleteAll(false);
        SiwzService.deleteAll(demandId, currentDemandValidation);
    };

    const onDownloadHistoryDialogClose = () => {
        setDownloadHistory(null);
    }

    const openDownloadHistory = (row, parentRowIdentity) => {
        setDownloadHistory(createDownloadHistoryFile(row.name, demandId, SiwzService.buildSiwzIdentity(row.identity, row.source, parentRowIdentity)));
    };

    const openEditDescription = (row, parentRowIdentity) => {
        setEditDescriptionFile(createEditDescriptionFile(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source, parentRowIdentity),
            row.name, row.description, !row.actions.editDescription));
    };

    const openEditNoticeReferenceNumber = (row) => {
        setEditNoticeReferenceNumberFile(createEditNoticeReferenceNumberFile(demandId, SiwzService.buildSiwzIdentity(row.identity, row.source), row.noticeReferenceNumber));
    }

    return (<>
        {!!siwzList && <div>
            {!siwzList.length ?
                <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
                :
                <>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.lpColumn}>{Tr("column_lp")}</TableCell>
                                <TableCell>{Tr("name")}</TableCell>
                                <TableCell className={classes.buttonColumn}>{Tr("description")}</TableCell>
                                <TableCell className={classes.typeColumn}>{Tr("type")}</TableCell>
                                <TableCell className={classes.buttonColumn}>{Tr("size")}</TableCell>
                                <TableCell className={classes.dateColumn}>{Tr("add_data")}</TableCell>
                                {isPublished && <TableCell className={classes.dateColumn}>{Tr("publish_data")}</TableCell>}
                                <TableCell className={classes.buttonColumn}></TableCell>
                                {showShare && <TableCell className={classes.buttonColumn}></TableCell>}
                                {isPublished && <TableCell className={classes.buttonColumn}></TableCell>}
                                {isPublished && <TableCell className={classes.buttonColumn}></TableCell>}
                                <TableCell className={classes.buttonColumn}>
                                    {!isPublished && <WorkableIconDeleteButton
                                        onClick={() => setOpenConfirmDeleteAll(true)}
                                        tooltip={Tr('delete_all')}
                                        disabled={disabled}
                                    />}
                                </TableCell>
                                {isPublished && <TableCell className={classes.buttonColumn}></TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                siwzList.map((doc, index) => (
                                    <SiwzFileTableRow
                                        key={doc.source + "_" + doc.identity}
                                        row={doc}
                                        demandId={demandId}
                                        index={index + 1}
                                        isPublished={isPublished}
                                        disabled={disabled}
                                        showShare={showShare}
                                        onEditDescriptionFile={openEditDescription}
                                        onEditNoticeReferenceNumber={openEditNoticeReferenceNumber}
                                        onOpenDownloadHistory={openDownloadHistory}
                                    />))
                            }
                        </TableBody>
                    </Table>

                    {downloadHistory &&
                    <DownloadHistoryPopup downloadHistoryFile={downloadHistory} onClose={onDownloadHistoryDialogClose} service={SiwzService}/>
                    }

                    {Boolean(editDescriptionFile) &&
                    <EditDescriptionPopup
                        editDescriptionFile={editDescriptionFile}
                        onUpdated={() => setEditDescriptionFile(null)}
                        onClose={() => setEditDescriptionFile(null)}
                        service={SiwzService}
                    />
                    }

                    {Boolean(editNoticeReferenceNumberFile) &&
                    <AddNoticeReferenceNumberPopup
                        editNoticeReferenceNumberFile={editNoticeReferenceNumberFile}
                        onUpdated={() => setEditNoticeReferenceNumberFile(false)}
                        onClose={() => setEditNoticeReferenceNumberFile(false)}
                        service={SiwzService}
                    />
                    }

                    <Confirm
                        open={Boolean(openConfirmDeleteAll)}
                        onConfirm={onDeleteAll}
                        onCancel={() => setOpenConfirmDeleteAll(false)}
                        confirmText="confirm_delete_all"
                    />
                </>
            }
        </div>
        }
    </>)
}

SiwzFileTable.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isPublished: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired
};
export default connect((state) => ({
    siwzList: state.resources[SiwzService.SIWZ_LIST_RESOURCE_ID],
    demandPrivileges: state.privileges[PrivilegeService.DEMAND_PRIVILEGE_STORE_KEY],
    demandHeader: state.resources.demandHeader
}), {})(SiwzFileTable);